import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Grid, IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import 'dayjs/locale/en-gb';

// Icone per il toggle edit/annulla
import EditIcon from "@mui/icons-material/Edit";

import {
    EditorialAdditionalInfo,
    EditorialAssetRenderParameters,
} from "../../../types/editorialAssetsTypes";
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";

interface ParametricFormProps {
    schema: EditorialAssetRenderParameters[];
    defaultValues?: Record<string, any>;
    onChange: (key: string, value: any) => void;
    onSubmit?: (data: Record<string, any>) => void;
    submitLabel?: string;
    infos?: Record<string, EditorialAdditionalInfo>;
    type: string;
    externalIsEditing?: boolean;
}

import DataGridBridge from "@/pages/custom-components/CommonComponents/DynamicDataGrid/DynamicDataGridBridge";
import { OverlayType } from "reactvideoeditor/pro/types";


const EditorialAssetForm: React.FC<ParametricFormProps> = ({
                                                               schema,
                                                               defaultValues = {},
                                                               onChange,
                                                               onSubmit = () => {},
                                                               infos,
                                                               type,
                                                               externalIsEditing = false
                                                           }) => {
    // Stato per gestire la modalità lettura/scrittura
    const [isEditing, setIsEditing] = useState(false);

    // Inizializza React Hook Form
    const { control, reset } = useForm({
        defaultValues,
        mode: "onChange",
    });

    // Aggiornamento da defaultValues esterni
    useEffect(() => {
        if (defaultValues && Object.keys(defaultValues).length > 0) {
            reset(defaultValues);
        }
    }, [defaultValues, reset]);

    // Gestione del toggle per la modalità edit
    const handleToggleEdit = () => {
        if (isEditing) {
            // Se stiamo annullando l'edit, resettiamo i campi ai valori di partenza
            reset(defaultValues);
        }
        setIsEditing(!isEditing);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box
                component="form"
                onSubmit={(e) => e.preventDefault()}
                sx={{ width: "100%", position: "relative" }}
            >
                {/* Header con bottone Edit/Annulla posizionato in alto a destra */}
                { type !== OverlayType.INTERLEAVE && <Box width={"100%"} display={"flex"} justifyContent={"flex-end"}>
                    <IconButton
                        onClick={handleToggleEdit}
                        color="secondary"
                    >
                        {isEditing ? <SaveIcon /> : <EditIcon />}
                    </IconButton>
                </Box>}

                <Grid container spacing={1.5} marginTop={1}>
                    {schema.map((fieldDef) => (
                            <Grid size={{ xs: 12 }} key={fieldDef.name}>
                                <Controller
                                    name={fieldDef.name}
                                    control={control}
                                    render={({ field }: any) => {
                                        const handleChange = (val: any) => {
                                            field.onChange(val);
                                            onChange(fieldDef.name, val);
                                        };

                                        switch (fieldDef.app_type) {
                                            case "string":
                                                return (
                                                    <TextField
                                                        fullWidth
                                                        label={fieldDef.label}
                                                        variant={"outlined"}
                                                        value={field.value || defaultValues.label || ""}
                                                        // ESTRAZIONE DEL VALORE DALL'EVENTO
                                                        onChange={(e) => handleChange(e.target.value)}
                                                        type="text"
                                                        disabled={(type !== OverlayType.INTERLEAVE) ? !isEditing : !externalIsEditing}
                                                    />
                                                );
                                            case "number":
                                                return (
                                                    <TextField
                                                        fullWidth
                                                        label={fieldDef.label}
                                                        variant={"outlined"}
                                                        value={field.value || ""}
                                                        onChange={(e) => handleChange(Number(e.target.value))}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        type="number"
                                                        disabled={(type !== OverlayType.INTERLEAVE) ? !isEditing : !externalIsEditing}
                                                    />
                                                );
                                            case "multistring":
                                                return (
                                                    <TextField
                                                        fullWidth
                                                        label={fieldDef.label}
                                                        variant={"outlined"}
                                                        // CORRETTO: da `values` a `value`
                                                        value={field.value || ""}
                                                        // ESTRAZIONE DEL VALORE DALL'EVENTO
                                                        onChange={(e) => handleChange(e.target.value)}
                                                        placeholder={isEditing ? `Add ${fieldDef.name}` : ""}
                                                        disabled={!isEditing}
                                                    />
                                                );

                                            case "date":
                                                return (
                                                    <DatePicker
                                                        label={fieldDef.label}
                                                        value={field.value ? dayjs(field.value) : null}
                                                        // QUI ANDAVA GIA' BENE perché il DatePicker restituisce l'oggetto Dayjs e non un SyntheticEvent
                                                        onChange={(newDate) => {
                                                            handleChange(newDate ? newDate.toISOString() : null);
                                                        }}
                                                        disabled={!isEditing}
                                                        slotProps={{
                                                            textField: {
                                                                variant: "standard",
                                                                fullWidth: true,
                                                                sx: {
                                                                    input: {
                                                                        color: !isEditing ? "rgba(255,255,255,0.6)" : "#fff",
                                                                    },
                                                                    label: { color: "rgba(255,255,255,0.7)" },
                                                                    svg: {
                                                                        color: !isEditing ? "rgba(255,255,255,0.4)" : "#fff",
                                                                    },
                                                                    borderBottom: "1px solid rgba(255,255,255,0.5)",
                                                                },
                                                            },
                                                        }}
                                                    />
                                                );
                                            case 'datagrid': {

                                                return (
                                                    <DataGridBridge
                                                        header={infos && infos[fieldDef.name] ? infos[fieldDef.name] : {}}
                                                        fieldDef={fieldDef}
                                                        value={field.value}
                                                        onChange={(newRawData: any) => handleChange(newRawData)}
                                                        isEditing={isEditing}
                                                    />
                                                );
                                            }

                                            default:
                                                return null;
                                        }
                                    }}
                                />
                            </Grid>
                        ))}
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default EditorialAssetForm;